// @ts-nocheck - init-trello.tsx bridges wac-web types (layout) and entries types which are incompatible at the boundary
import React from "react";

import { parse } from "cookie";
import { createRoot, hydrateRoot } from "react-dom/client";
import { StyleSheetManager } from "styled-components";
import { v4 as uuidv4 } from "uuid";

import { initializeFeatureGatesClient } from "@/feature-gates/initialization";
import {
    extractOptanonConsentCookie,
    isPerformanceOnetrustEnabled
} from "@/utils/onetrust-provider";
import type { LocationData } from "@/utils/useGeoCurrency";
import { processExperimentAndFeatureGateOverrides } from "@/utils/wac-browser-utility";

import { getAnalyticsWebClientTrello } from "./analytics/ufo/get-analytics-web-client-trello";
import { initUFOTrello } from "./analytics/ufo/init-ufo-trello";
import { App } from "./entries/app";
import { PageProps } from "./layout";
import { Layout } from "./layout";
import { NotFoundPage } from "./pages/not-found-page";

window.featureGateBootstrapValues = window.featureGateBootstrapValues || {
    identifiers: {
        analyticsAnonymousId: uuidv4()
    }
};

// Single component for SSR/hydration path
const TrelloPageRenderer = (props: PageProps) => {
    if (props.isNotFound) {
        return <NotFoundPage pageModel={props.pageModel} />;
    }
    if (!props.pageModel) {
        return null;
    }

    return <App defaultPageModel={props.pageModel} />;
};

export const init = (_Page: React.ComponentType, props) => {
    // Guard against SSR environment.
    if (typeof window === "undefined") {
        return;
    }

    // ---------------------------------------------------------------------
    const isStatic = window.IS_STATIC;

    // OneTrust / CPRA performance-cookie consent. If performance cookies
    // are disabled, exposure events must NOT be fired (privacy compliance).
    // LocationData is supplied by the SSR template via window.countryCode /
    // window.regionIsoCode (see trello-template.tsx); mirrors wac-web
    // init-streaming.tsx pattern.
    const locationData: LocationData = {
        countryIsoCode: window.countryCode || "",
        regionIsoCode: window.regionIsoCode || ""
    };
    const isPerfOnetrustEnabled = isPerformanceOnetrustEnabled(
        extractOptanonConsentCookie(document.cookie),
        locationData
    );

    // Keep production consent behavior, but allow local/dev debugging without OneTrust cookies.
    const shouldEnableAnalyticsClient =
        process.env["CLIENT_LIBRARY_ENV"] !== "production" ||
        isPerfOnetrustEnabled;

    const analyticsWebClient = shouldEnableAnalyticsClient
        ? getAnalyticsWebClientTrello()
        : null;

    // Initialize UFO imperatively BEFORE React renders — same pattern as wac-web init-streaming.tsx.
    // traceUFOPageLoad must fire AFTER initReactUFO so the subscriber is ready to capture it.
    if (analyticsWebClient) {
        initUFOTrello(analyticsWebClient, window.location.pathname);
    }

    const parsedClientCookies = document.cookie ? parse(document.cookie) : {};
    const userAgent = window.navigator.userAgent;

    const { finalExperimentOverrides, finalFeatureGateOverrides } =
        processExperimentAndFeatureGateOverrides(
            parsedClientCookies,
            userAgent,
            isPerfOnetrustEnabled
        );

    initializeFeatureGatesClient(window.featureGateBootstrapValues, {
        overrideValues: {
            experimentOverrides: finalExperimentOverrides,
            featureGateOverrides: finalFeatureGateOverrides
        },
        // If performance cookies are disabled, run in localMode so the SDK
        // still resolves gate values but does NOT fire exposure events.
        localMode: !isPerfOnetrustEnabled
    }).then(() => {
        const pageModel =
            props?.defaultPageModel ||
            (window as Window & { __PAGE_MODEL__?: unknown }).__PAGE_MODEL__ ||
            null;

        const isNotFound = !!(window as Window & { __IS_NOT_FOUND__?: boolean })
            .__IS_NOT_FOUND__;

        const rootElement = document.getElementById("trello-portal-root");

        // Filter out custom props that shouldn't be passed to DOM elements
        const shouldForwardProp = (prop: string) => {
            const blockedProps = [
                "iconColorInitial",
                "themeInitial",
                "hoverColorInitial",
                "theme",
                "overflowingBreakpoints",
                "hideLanguageSelector"
            ];
            return !blockedProps.includes(prop);
        };

        const page = (
            <StyleSheetManager shouldForwardProp={shouldForwardProp}>
                <Layout
                    analyticsWebClient={analyticsWebClient}
                    location={window.location}
                    userProfile={undefined}
                    userProfileStatus={undefined}
                    countryCode={undefined}
                    hasCloudSessionTokenCookie={false}
                    userAgent={navigator.userAgent}
                    pageModel={pageModel}
                    isNotFound={isNotFound}
                >
                    {TrelloPageRenderer}
                </Layout>
            </StyleSheetManager>
        );

        if (isStatic) {
            const trelloElement = document.getElementById("trello");
            if (!trelloElement) {
                throw new Error("Trello root element not found");
            }
            const root = createRoot(trelloElement);
            root.render(page);
        } else {
            if (!rootElement || !(rootElement instanceof Element)) {
                throw new Error(
                    "[init-trello] trello-portal-root is not a valid DOM element. Type: " +
                        typeof rootElement
                );
            }

            hydrateRoot(rootElement, page, {
                onRecoverableError: () => {
                    // add an attribute if in CI
                    if (process.env["CI"] === "true") {
                        rootElement.setAttribute("hydration-error", "true");
                    }
                }
            });
        }
    });
};

declare global {
    interface Window {
        IS_STATIC?: boolean;
        countryCode?: string;
        regionIsoCode?: string;
    }
}
